import { acceptHMRUpdate, defineStore } from 'pinia';
import isEqual from 'lodash/isEqual';
import set from 'lodash/set';
import { useAppsStore } from '@/stores/apps.store';
import { useGlobalFiltersStore } from '@/stores/global-filters.store';
import { defaultSelectedAccount } from '@/stores/helpers/global-filters.helper';
import { useGroupsStore } from '@/stores/groups.store';
import { useStatusesStore } from '@/stores/statuses.store';
import { useSurveysStore } from '@/stores/surveys.store';
import { useUserSettingsStore } from '@/stores/user-settings.store';
import { fetchMinimalUsers } from '@/stores/api/users.api';
import { useAuthStore } from '@/stores/auth.store';
import { useSubscriptionStore } from '@/stores/subscription.store';
import { useVocItemsTableStore } from '@/stores/voc-items-table.store';
import { useVocLabelsStore } from '@/stores/voc-labels.store';
import { useVocPortalStore } from '@/stores/voc-portal.store';
import { storeFilters } from '@/stores/helpers/voc-items.helper';
import { convertConfigToTimeSeries } from '@/common/time-series-picker/time-series-picker.util';
import { npsFilters } from '@/net-promoter-system/constants/nps.constants';
import { PerSubscriptionUserStorage } from '@utils/storage';
import { runAggregationOnce, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { i18n_t as $t } from '@/utils/i18n';
import VocItemsCreatedByAdditionalUsersAggregation from '@/voc/aggregations/voc-item-created-by-visitors/voc-item-created-by-additional-users.aggregation';
import VocItemsSourcesAggregation from '@/voc/aggregations/voc-item-sources/voc-item-sources.aggregation';
import { FEEDBACK_IMPORTANCES } from '@/voc/constants/constants';
import { formatLabels, formatVocItemFilterForAgg } from '@/voc/helpers/common.helper';

const { CAPTURE, VALIDATE } = SUB_PRODUCT_AREAS || {};
const isJestTest = !!process.env.JEST_WORKER_ID;

interface SortConfig {
    order: string;
    prop: string;
}

interface UserChoice {
    divided?: boolean;
    id: string;
    label: string;
    type?: string;
}

interface UserChoiceGroup {
    category: string;
    id: string;
    users: UserChoice[];
}

interface SourceChoice {
    guideName?: string;
    sourceCategory?: string;
    sourceIdentifier?: string;
    [key: string]: unknown;
}

interface FilterChoices {
    accountTypes: unknown[];
    alert: string[];
    assignedToUsers: UserChoice[];
    createdByUsers: Array<UserChoice | UserChoiceGroup>;
    feedbackStatuses: unknown[];
    importance: unknown[];
    labels: unknown[];
    linkedIdeas: Array<{ id: boolean; label: string }>;
    linkedJiraIssues: Array<{ id: boolean; label: string }>;
    npsScores: unknown[];
    portals: unknown[];
    sources: SourceChoice[];
}

type FilterMap = Record<string, unknown>;

interface VocItemsFiltersState {
    choices: FilterChoices;
    choicesLoaded: boolean;
    choicesLoadedType: string | undefined;
    emptyFilters: Record<string, FilterMap>;
    emptyFiltersMinusGlobal: Record<string, FilterMap>;
    map: FilterMap;
    promises: Record<string, Promise<void> | null | undefined>;
    prompt: string;
    storedFiltersLoaded: boolean;
    type: string | undefined;
    persistFilters: boolean;
    inMemoryFiltersInitialized: boolean;
}

interface UpdateFilterPayload {
    key: string;
    persist?: boolean;
    persistGlobal?: boolean;
    value: unknown;
}

interface UpdateFiltersPayload {
    filters: FilterMap;
    persist?: boolean;
    persistGlobal?: boolean;
    reset?: boolean;
}

interface StoredFilters {
    value?: FilterMap;
}

interface MinimalUser {
    id: string;
    username: string;
}

interface AdditionalUser extends MinimalUser {
    type: string;
}

let sort: SortConfig = { order: 'none', prop: '' };

export function __resetSortForUnitTests (): void {
    sort = { order: 'none', prop: '' };
}

export function getInitialState (): VocItemsFiltersState {
    return {
        map: {},
        choices: {
            feedbackStatuses: [],
            createdByUsers: [],
            sources: [],
            importance: FEEDBACK_IMPORTANCES,
            labels: [],
            linkedIdeas: [
                {
                    id: true,
                    label: 'Is linked to an Idea',
                },
                {
                    id: false,
                    label: 'Is not linked to an Idea',
                },
            ],
            linkedJiraIssues: [
                {
                    id: true,
                    label: 'Is linked to a Jira issue',
                },
                {
                    id: false,
                    label: 'Is not linked to a Jira issue',
                },
            ],
            alert: ['Blocker to Sale', 'High Frustration', 'Churn Risk'],
            npsScores: npsFilters.rating,
            assignedToUsers: [],
            accountTypes: [],
            portals: [],
        },
        choicesLoaded: false,
        choicesLoadedType: undefined,
        storedFiltersLoaded: false,
        type: undefined,
        // When false, filters live only in memory for the life of the page (no durable server
        // blob): they survive tab switches but are cleared via `resetFilters` on page leave.
        persistFilters: true,
        // Tracks whether the in-memory (non-persistent) map has been seeded this page lifetime,
        // so remounts keep the current selections instead of re-seeding to blank.
        inMemoryFiltersInitialized: false,
        emptyFilters: {
            feedbackExplore: {
                allTime: true,
                segmentId: 'everyone',
            },
            feedback: {
                groups: [],
                feedbackStatuses: [],
                createdBy: [],
                sources: [],
                importance: [],
                labels: [],
                linkedIdeas: [],
                linkedJiraIssues: [],
                appIds: ['*'],
                segmentId: 'everyone',
                segmentIdInvalid: false,
                allTime: true,
                timeSeries: isJestTest
                    ? null
                    : convertConfigToTimeSeries({ type: 'Last 30 Days', start: null, end: null }).config,
                searchText: '',
                account: '',
                npsScores: [],
                feedbackType: [],
                alert: [],
                assignee: [],
                accountTypes: [],
            },
            ideas: {
                groups: [],
                statuses: [],
                createdBy: [],
                importance: [],
                labels: [],
                workarounds: [],
                linkedJiraIssues: [],
                app: '*',
                segment: null,
                allTime: true,
                searchText: '',
                account: { account: defaultSelectedAccount },
                assignee: [],
                accountTypes: [],
                portals: [],
            },
        },
        emptyFiltersMinusGlobal: {
            feedbackExplore: {},
            feedback: {
                groups: [],
                feedbackStatuses: [],
                createdBy: [],
                sources: [],
                importance: [],
                labels: [],
                linkedIdeas: [],
                linkedJiraIssues: [],
                searchText: '',
                npsScores: [],
                assignee: [],
                accountTypes: [],
            },
            ideas: {
                groups: [],
                statuses: [],
                createdBy: [],
                workarounds: [],
                linkedJiraIssues: [],
                importance: [],
                labels: [],
                searchText: '',
                assignee: [],
                accountTypes: [],
                portals: [],
            },
        },
        promises: {},
        prompt: '',
    };
}

export const useVocItemsFiltersStore = defineStore('vocItems/filters', {
    state: getInitialState,
    getters: {
        filters: (state): FilterMap => state.map || {},
        filtersForAgg: (state): FilterMap =>
            Object.entries(state.map || {}).reduce<FilterMap>((processedFilters, [key, filter]) => {
                const formattedFilter = formatVocItemFilterForAgg(key, filter);

                if (formattedFilter) {
                    processedFilters[formattedFilter.key] = formattedFilter.value;
                }

                return processedFilters;
            }, {}),
        allFilters: (state): FilterMap => {
            const globalFiltersStore = useGlobalFiltersStore();

            return {
                ...state.map,
                ...(state.type === 'ideas' && {
                    segment: globalFiltersStore.segmentId,
                    timeSeries: globalFiltersStore.timeSeries,
                    allTime: globalFiltersStore.allTime,
                    app: globalFiltersStore.selectedApp?.id,
                    account: globalFiltersStore.selectedAccount,
                }),
            };
        },
        feedbackEmptyFilters: (state): FilterMap => state.emptyFilters.feedback,
        feedbackExploreEmptyFilters: (state): FilterMap => state.emptyFilters.feedbackExplore,
        filterByKey: (state): ((filterKey: string) => unknown) => (filterKey) => state.map[filterKey],
        formattedChoices: (state): FilterChoices => ({
            ...state.choices,
            feedbackStatuses: useStatusesStore().list('feedback').map((status) => {
                const style = status.style as { dotColor?: string };

                return {
                    id: status.id,
                    name: status.name,
                    icon: {
                        type: 'circle',
                        size: '8',
                        fill: style.dotColor,
                        stroke: 0,
                        class: 'status-circle',
                    },
                };
            }),
            labels: formatLabels(useVocLabelsStore().feedbackLabels),
        }),
    },
    actions: {
        setMap ({ filters }: { filters: FilterMap }) {
            this.map = filters;
        },
        updateMap ({ key, value }: { key: string; value: unknown }) {
            if (key === 'allTime' && value === true && this.map.timeSeries) {
                delete this.map.timeSeries;
            } else if (
                key === 'allTime' &&
                value === false &&
                !this.map.timeSeries &&
                this.emptyFilters[this.type || '']?.timeSeries
            ) {
                this.map.timeSeries = this.emptyFilters[this.type || ''].timeSeries;
            }

            this.map = {
                ...this.map,
                [key]: value,
            };
        },
        updateStoredFiltersLoaded (value: boolean) {
            this.storedFiltersLoaded = value;
        },
        setType (type: string) {
            this.type = type;
        },
        setPersistFilters (persistFilters: boolean) {
            this.persistFilters = persistFilters;
        },
        setInMemoryFiltersInitialized (value: boolean) {
            this.inMemoryFiltersInitialized = value;
        },
        updateChoices (choices: Partial<FilterChoices> = {}) {
            this.choices = {
                ...this.choices,
                ...choices,
            };
        },
        setChoicesLoaded (value: boolean) {
            this.choicesLoaded = value;
        },
        setPromise ({ promise, key }: { promise: Promise<void> | null; key: string | string[] }) {
            set(this.promises, key, promise);
        },
        setPrompt ({ prompt }: { prompt: string }) {
            this.prompt = prompt;
        },
        loadAll ({ type = 'idea', persist = true }: { type?: string; persist?: boolean } = {}) {
            this.setType(type);
            this.setPersistFilters(persist);
            this.setChoicesLoaded(false);

            if (type === 'feedback' || type === 'feedbackExplore') {
                const filters = { ...this.emptyFilters[type] };

                if (filters.allTime) {
                    delete filters.timeSeries;
                }

                this.setMap({ filters });
                this.updateStoredFiltersLoaded(true);

                return;
            }

            if (!persist) {
                // In-memory context: seed blank once, then keep the current map across remounts
                // (e.g. tab switches) so selections survive without a durable server blob.
                if (!this.inMemoryFiltersInitialized) {
                    this.setMap({ filters: { groups: [] } });
                    this.setInMemoryFiltersInitialized(true);
                }
                this.updateStoredFiltersLoaded(true);

                return;
            }

            this.updateStoredFiltersLoaded(false);

            return Promise.all([
                PerSubscriptionUserStorage.get('vocListExpansionFilters'),
                useUserSettingsStore().loadOne({
                    namespaced: true,
                    name: 'groupFilter',
                }),
            ]).then(([storedFilters = {}]) => {
                const savedGroupFilter = useGroupsStore().storedUserSettingGroupFilter;
                const filters = storedFilters as StoredFilters;

                this.setMap({
                    filters: {
                        ...(filters?.value || {}),
                        groups: savedGroupFilter,
                    },
                });
                this.updateStoredFiltersLoaded(true);
            });
        },
        loadChoices ({ type = 'idea' }: { type?: string } = {}) {
            if (this.promises.choices) {
                return this.promises.choices;
            }

            if (this.choicesLoaded && this.choicesLoadedType === type) {
                return Promise.resolve();
            }

            VocItemsSourcesAggregation.subProductArea = type === 'idea' ? VALIDATE : CAPTURE;

            const basePromises = [
                fetchMinimalUsers(),
                runAggregationOnce(VocItemsSourcesAggregation, { type }).catch(() => undefined),
                useVocPortalStore().loadPortals(),
            ];

            const surveysPromise = useSurveysStore().loadAll().catch(() => undefined);
            const visitorsPromise =
                type === 'feedback'
                    ? (async () => {
                        let visitorDisplayName: string | null = null;
                        if (useAuthStore().hasSegmentFlag('visitorDisplayNameMapping')) {
                            await useSubscriptionStore()
                                .loadSetting({ setting: 'visitorDisplayName', retrieveMetadata: false })
                                .catch((error: unknown) => error);
                            visitorDisplayName =
                                (useSubscriptionStore().settings.visitorDisplayName?.value as string | undefined) || null;
                        }

                        return runAggregationOnce(VocItemsCreatedByAdditionalUsersAggregation, { type, visitorDisplayName });
                    })().catch(() => [])
                    : Promise.resolve([]);

            const promise = Promise.all([...basePromises, surveysPromise, visitorsPromise])
                .then(([minimalUsersResult, sourcesResult, , , visitorsResult]) => {
                    const { portals } = useVocPortalStore();
                    const minimalUsersList = (minimalUsersResult || []) as MinimalUser[];
                    const sources = (sourcesResult || []) as SourceChoice[];
                    const visitorsList = (visitorsResult || []) as AdditionalUser[];

                    sources.forEach((source) => {
                        if (source.sourceCategory === 'Surveys' && source.sourceIdentifier) {
                            const survey = useSurveysStore().surveyById(source.sourceIdentifier) as { name?: string };
                            source.guideName = survey?.name || '(Unavailable Survey)';
                        }
                    });

                    const mappedUsers = minimalUsersList.map((user) => ({
                        id: user.id,
                        label: user.username,
                    }));

                    const createdByUsers =
                        type === 'feedback'
                            ? (() => {
                                const mappedVisitors = visitorsList.map((visitor) => ({
                                    id: visitor.id,
                                    label: visitor.username ?? visitor.id,
                                    type: visitor.type,
                                }));

                                return [
                                    {
                                        id: 'User',
                                        category: $t('filters.createdBy.userLabel'),
                                        users: mappedUsers.map((user) => ({ ...user, divided: false })),
                                    },
                                    {
                                        id: 'Visitor',
                                        category: $t('filters.createdBy.visitor'),
                                        users: mappedVisitors.filter((visitor) => visitor.type === 'Visitor'),
                                    },
                                    {
                                        id: 'IntegrationKey',
                                        category: $t('filters.createdBy.integrationKey'),
                                        users: mappedVisitors.filter((visitor) => visitor.type === 'IntegrationKey'),
                                    },
                                ];
                            })()
                            : mappedUsers;

                    this.updateChoices({
                        createdByUsers,
                        sources,
                        assignedToUsers: mappedUsers,
                        portals: portals || [],
                    });
                    this.setChoicesLoaded(true);
                    this.choicesLoadedType = type;
                })
                .finally(() => {
                    this.setPromise({ key: 'choices', promise: null });
                });

            this.setPromise({ key: 'choices', promise });

            return promise;
        },
        updateFilter ({ key, value, persist = true, persistGlobal = false }: UpdateFilterPayload) {
            const globalKeys = ['app', 'segment', 'timeSeries', 'allTime', 'account'];

            if (globalKeys.includes(key)) {
                if (persistGlobal) {
                    const globalFiltersStore = useGlobalFiltersStore();
                    switch (key) {
                        case 'app': {
                            const selectedAppId = (globalFiltersStore.selectedApps as unknown as { id?: string })?.id;
                            if (!isEqual(selectedAppId, value)) {
                                const selectedApp = useAppsStore().getAppOrCategoryById(value as string);
                                selectedApp.id = value === '*' ? '*' : selectedApp.id;
                                globalFiltersStore.setSelectedApp({ selectedApp, updateAngular: true });
                            }
                            break;
                        }
                        case 'segment': {
                            const selectedSegmentId = globalFiltersStore.segmentId;
                            if (!isEqual(selectedSegmentId, value)) {
                                const payload = { selectedSegmentId: value as string, fromVue: true };
                                globalFiltersStore.setSelectedSegmentId(payload);
                            }
                            break;
                        }
                        case 'timeSeries': {
                            const selectedTimeSeries = globalFiltersStore.timeSeries;
                            if (value && !isEqual(selectedTimeSeries, value)) {
                                const payload = {
                                    timeSeries: value as Parameters<typeof globalFiltersStore.setTimeSeries>[0]['timeSeries'],
                                    fromVue: true,
                                };
                                globalFiltersStore.setTimeSeries(payload);
                            }
                            break;
                        }
                        case 'allTime': {
                            const selectedAllTime = globalFiltersStore.allTime;
                            if (!isEqual(selectedAllTime, value)) {
                                globalFiltersStore.setAllTime({ allTime: value as boolean });
                            }
                            break;
                        }
                        case 'account': {
                            const selectedAccount = globalFiltersStore.selectedAccountAccount;
                            const account = value as { account?: unknown };
                            if (!isEqual(selectedAccount, account?.account)) {
                                globalFiltersStore.setSelectedAccount({
                                    selectedAccount: account,
                                    updateAngular: true,
                                });
                            }
                            break;
                        }
                    }
                }
                this.updateMap({ key, value });
            } else if (key === 'groups' && !isEqual(this.map[key], value)) {
                this.updateMap({ key, value });
            } else if (!isEqual(this.map[key], value) && value !== undefined) {
                const storedFilters = Object.keys(this.map)
                    .filter((filterKey) => filterKey !== 'groups')
                    .reduce<FilterMap>((currentFilters, filterKey) => {
                        currentFilters[filterKey] = this.map[filterKey];

                        return currentFilters;
                    }, {});

                const filters = {
                    ...storedFilters,
                    [key]: value,
                };

                this.updateMap({ key, value });

                // Only durable contexts write the server-side blob; in-memory contexts keep
                // selections in the store map for the life of the page.
                if (persist && this.persistFilters) {
                    (storeFilters as unknown as (payload: { filters: FilterMap }) => void)({ filters });
                }
                if (key === 'searchText') {
                    const vocItemsTableStore = useVocItemsTableStore();
                    const currentSort = vocItemsTableStore.tableConfig.sort as SortConfig;

                    if (currentSort?.order !== 'none') {
                        sort = currentSort;
                    }

                    if ((value as string).length > 0) {
                        vocItemsTableStore.update({ sort: { prop: currentSort.prop, order: 'none' } });
                    } else {
                        vocItemsTableStore.update({
                            sort: {
                                prop: sort.prop ?? currentSort.prop,
                                order: sort.order ?? currentSort.order,
                            },
                        });
                    }
                }
            }
        },
        updateFilters ({ filters, persist = true, persistGlobal = true, reset = false }: UpdateFiltersPayload) {
            if (reset) {
                this.reset({ persist, persistGlobal });
            }
            Object.keys(filters).forEach((key) => {
                this.updateFilter({ key, value: filters[key], persist, persistGlobal });
            });
        },
        updatePrompt ({ prompt }: { prompt: string }) {
            this.setPrompt({ prompt });
        },
        reset (
            { persist = false, persistGlobal = false }: { persist?: boolean; persistGlobal?: boolean } = {
                persist: false,
                persistGlobal: false,
            },
        ) {
            const emptyFilters = this.emptyFiltersMinusGlobal[this.type || ''] || {};
            if (Object.keys(emptyFilters).length) {
                this.updateFilters({ filters: emptyFilters, persist, persistGlobal });
            } else {
                this.setMap({ filters: {} });
            }
        },
        // Fully clears an in-memory context on page leave so the next entry seeds blank.
        resetFilters () {
            this.setMap({ filters: {} });
            this.setInMemoryFiltersInitialized(false);
            this.updateStoredFiltersLoaded(false);
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useVocItemsFiltersStore, module.hot));
}
